import Seo from "../components/Seo";

/** Google Play submission ke liye MANDATORY Privacy Policy page.
 *  Covers: personal data, payments (Razorpay), analytics (Meta Pixel), auth (JWT),
 *  location, image uploads, chat data — all real data flows in the app. */
export default function Privacy() {
    const updated = "February 8, 2026";
    return (
        <div className="min-h-screen bg-cream-50">
            <Seo title="Privacy Policy — Global Property Mart" description="How Global Property Mart collects, uses and protects your personal information." path="/privacy" />
            <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10 lg:py-16 text-navy-500">
                <h1 className="font-heading text-4xl font-extrabold">Privacy Policy</h1>
                <p className="text-sm text-navy-500/60 mt-2">Last updated: {updated}</p>

                <Section title="1. Who We Are">
                    Global Property Mart ("we", "us", "our") is operated in Nagpur, Maharashtra, India.
                    We provide an online real-estate marketplace and this Privacy Policy explains how
                    we collect, use, share and protect your personal information when you use our
                    website <a className="text-gold-700 underline" href="https://globalpropertymart.com">globalpropertymart.com</a> or our Android application.
                </Section>

                <Section title="2. Information We Collect">
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Account data</strong>: name, email, phone number, password (bcrypt-hashed), role (buyer / seller / broker / advocate / loan agent / architect / interior designer / civil contractor / admin).</li>
                        <li><strong>Property data</strong>: listings, photos, descriptions, prices, locations you post.</li>
                        <li><strong>Communication data</strong>: inquiries, chat messages, support tickets you send through our platform.</li>
                        <li><strong>Payment data</strong>: subscription plan, transaction ID, timestamps. Card / UPI credentials are handled entirely by Razorpay — we never see or store them.</li>
                        <li><strong>Analytics & technical data</strong>: IP address, device type, browser, pages viewed, referring URL, Meta Pixel identifiers.</li>
                        <li><strong>Location data</strong>: only city / locality you enter voluntarily; we do not collect precise GPS.</li>
                    </ul>
                </Section>

                <Section title="3. How We Use Your Data">
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Provide the marketplace and match buyers with sellers, brokers and partners.</li>
                        <li>Process subscription payments via Razorpay (a PCI-DSS certified processor).</li>
                        <li>Send transactional email notifications (inquiry alerts, auction alerts, price drops, upgrade reminders) via Resend.</li>
                        <li>Improve the product with aggregate analytics (Meta Pixel / Conversions API) — event-level data is hashed (SHA-256) before leaving our servers.</li>
                        <li>Detect fraud, prevent abuse, comply with legal obligations (Indian IT Act 2000, GST, RERA).</li>
                    </ul>
                </Section>

                <Section title="4. Third-Party Services">
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Razorpay</strong> — payment processing (<a href="https://razorpay.com/privacy" className="text-gold-700 underline">privacy policy</a>)</li>
                        <li><strong>Meta (Facebook) Pixel + Conversions API</strong> — advertising analytics (<a href="https://www.facebook.com/privacy/policy" className="text-gold-700 underline">privacy policy</a>)</li>
                        <li><strong>Resend</strong> — transactional email delivery (<a href="https://resend.com/privacy" className="text-gold-700 underline">privacy policy</a>)</li>
                        <li><strong>Emergent Object Storage</strong> — image hosting for property photos</li>
                    </ul>
                </Section>

                <Section title="5. Data Sharing">
                    We do <strong>not</strong> sell your personal data. We share limited information with:
                    <ul className="list-disc pl-6 space-y-1 mt-2">
                        <li>Property owners / brokers when you submit an inquiry (name, phone, message).</li>
                        <li>The service providers listed above under a written data-processing agreement.</li>
                        <li>Government authorities when required by law (court order, RERA compliance, tax).</li>
                    </ul>
                </Section>

                <Section title="6. Your Rights">
                    You may at any time: (a) access, correct or delete your data; (b) download a copy of your data; (c) withdraw consent to marketing communications; (d) close your account. Email us at <a className="text-gold-700 underline" href="mailto:globalpropertymart95@gmail.com">globalpropertymart95@gmail.com</a> and we will respond within 30 days.
                </Section>

                <Section title="7. Data Retention">
                    We keep your account data for as long as your account is active and for up to 3 years after closure for legal / accounting purposes. Payment records are retained for 8 years to comply with Indian tax law.
                </Section>

                <Section title="8. Security">
                    Passwords are hashed with bcrypt. All traffic is served over HTTPS (TLS 1.3). Access to production data is restricted to authorised employees. We never store card / UPI credentials on our servers.
                </Section>

                <Section title="9. Children">
                    Global Property Mart is not intended for children under 18. We do not knowingly collect data from minors.
                </Section>

                <Section title="10. Changes to This Policy">
                    We may update this policy from time to time. The "Last updated" date at the top will reflect the change. Material changes will be notified via email.
                </Section>

                <Section title="11. Contact">
                    Global Property Mart<br />
                    Nagpur, Maharashtra, India<br />
                    Email: <a className="text-gold-700 underline" href="mailto:globalpropertymart95@gmail.com">globalpropertymart95@gmail.com</a>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mt-8">
            <h2 className="font-heading text-xl font-bold text-navy-500">{title}</h2>
            <div className="mt-2 text-sm leading-relaxed text-navy-500/85">{children}</div>
        </section>
    );
}
